import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { LeadAreaCard } from "@/components/LeadAreaCard";
import { ArrowLeft, BadgeCheck, MapPin, X, Truck, ExternalLink, Bell, Image as ImageIcon, Upload } from "lucide-react";
import { toast } from "sonner";

const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];
const FREQ = [{ v: "instant", l: "Instant" }, { v: "daily", l: "Daily digest" }, { v: "off", l: "Off" }];

export default function RemovalistProfile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "", phone: user?.phone || "",
    company_name: user?.company_name || "", abn_or_license: user?.abn_or_license || "",
    website: user?.website || "", insurance: user?.insurance || "",
  });
  const [logoPath, setLogoPath] = useState(user?.logo_path || "");
  const [logoPreview, setLogoPreview] = useState(user?.logo_path ? `${API}/public/logo/${user.id}?t=${Date.now()}` : "");
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoPath(data.path);
      setLogoPreview(URL.createObjectURL(file));
      toast.success("Logo uploaded — save profile to apply");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); e.target.value = ""; }
  };
  const [coverage, setCoverage] = useState(user?.coverage || []);
  const [coverageQuery, setCoverageQuery] = useState("");
  const [prefs, setPrefs] = useState(user?.email_prefs || { new_job: "instant", bid_accepted: "instant" });
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addArea = (r) => {
    const exists = coverage.some((c) => c.suburb === r.suburb && c.postcode === r.postcode && c.state === r.state);
    if (!exists) setCoverage((s) => [...s, { suburb: r.suburb, state: r.state, postcode: r.postcode, lat: r.lat, lon: r.lon }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", { ...form, logo_path: logoPath, coverage, email_prefs: prefs });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-asphalt flex items-center justify-center"><Truck className="h-6 w-6 text-white" /></div>
            <div>
              <h1 className="font-display text-3xl font-bold flex items-center gap-2">Business Profile {user?.is_verified && <BadgeCheck className="h-6 w-6 text-emerald-500" />}</h1>
              {user?.is_verified && <p className="text-sm text-emerald-600 font-medium">Verified removalist</p>}
            </div>
          </div>
          {user?.id && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/removalists/${user.id}`)} data-testid="view-public-profile-btn" className="gap-1"><ExternalLink className="h-4 w-4" /> Public profile</Button>
          )}
        </div>

        <Card className="p-6 space-y-4 mb-6">
          <h2 className="font-display font-semibold text-lg">Company details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Contact name</Label><Input data-testid="profile-name-input" value={form.name} onChange={upd("name")} /></div>
            <div className="space-y-2"><Label>Phone</Label><Input data-testid="profile-phone-input" value={form.phone} onChange={upd("phone")} /></div>
            <div className="space-y-2"><Label>Company name</Label><Input data-testid="profile-company-input" value={form.company_name} onChange={upd("company_name")} placeholder="Carter Removals Pty Ltd" /></div>
            <div className="space-y-2"><Label>ABN / License</Label><Input data-testid="profile-abn-input" value={form.abn_or_license} onChange={upd("abn_or_license")} placeholder="ABN 12 345 678 901" /></div>
            <div className="space-y-2"><Label>Website</Label><Input data-testid="profile-website-input" value={form.website} onChange={upd("website")} placeholder="www.carterremovals.com.au" /></div>
            <div className="space-y-2"><Label>Insurance coverage</Label><Input data-testid="profile-insurance-input" value={form.insurance} onChange={upd("insurance")} placeholder="Public liability $20M · Transit insurance $100k" /></div>
          </div>
        </Card>

        <Card className="p-6 space-y-4 mb-6">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><ImageIcon className="h-5 w-5 text-kinetic" /> Company logo</h2>
            <p className="text-sm text-muted-foreground">Shown on your public profile and next to your bids. PNG, JPG or WebP, up to 2MB.</p>
          </div>
          <div className="flex items-center gap-5">
            <div className="h-24 w-24 rounded-xl border border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden" data-testid="logo-preview">
              {logoPreview ? <img src={logoPreview} alt="Company logo" className="h-full w-full object-contain" /> : <ImageIcon className="h-8 w-8 text-muted-foreground" />}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadLogo} data-testid="logo-file-input" />
              <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn" className="gap-1"><Upload className="h-4 w-4" /> {uploading ? "Uploading…" : logoPath ? "Replace logo" : "Upload logo"}</Button>
              {logoPath && <Button type="button" variant="ghost" size="sm" onClick={() => { setLogoPath(""); setLogoPreview(""); }} data-testid="remove-logo-btn" className="text-destructive gap-1"><X className="h-4 w-4" /> Remove</Button>}
            </div>
          </div>
        </Card>

        <LeadAreaCard />

        <Card className="p-6 space-y-4 mb-6">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><MapPin className="h-5 w-5 text-kinetic" /> Service areas</h2>
            <p className="text-sm text-muted-foreground">Search & add the suburbs or addresses you cover. You'll be alerted about matching jobs.</p>
          </div>
          <AddressAutocomplete testid="coverage-suburb-input" value={coverageQuery}
            onChange={setCoverageQuery}
            onSelect={(r) => { addArea(r); setCoverageQuery(""); }}
            placeholder="Search a suburb or address to add…" />
          <div className="flex flex-wrap gap-2">
            {coverage.length === 0 && <p className="text-sm text-muted-foreground">No service areas added yet.</p>}
            {coverage.map((c, i) => (
              <Badge key={i} data-testid={`coverage-${i}`} variant="outline" className="gap-1 py-1.5 px-3 bg-kinetic-light border-kinetic/30 text-asphalt">
                {c.suburb}{c.state ? `, ${c.state}` : ""}{c.postcode ? ` ${c.postcode}` : ""}
                <button type="button" onClick={() => setCoverage((s) => s.filter((_, idx) => idx !== i))}><X className="h-3 w-3" /></button>
              </Badge>
            ))}
          </div>
        </Card>

        <Card className="p-6 space-y-4 mb-6">
          <div>
            <h2 className="font-display font-semibold text-lg flex items-center gap-2"><Bell className="h-5 w-5 text-kinetic" /> Email preferences</h2>
            <p className="text-sm text-muted-foreground">Choose which alerts land in your inbox and how often.</p>
          </div>
          <div className="flex items-center justify-between gap-4 py-1">
            <div><p className="font-medium text-sm">New job alerts</p><p className="text-xs text-muted-foreground">Jobs posted in your service areas</p></div>
            <Select value={prefs.new_job || "instant"} onValueChange={(v) => setPrefs((p) => ({ ...p, new_job: v }))}>
              <SelectTrigger data-testid="pref-new-job" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">{FREQ.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-4 py-1 border-t border-border pt-4">
            <div><p className="font-medium text-sm">Bid accepted</p><p className="text-xs text-muted-foreground">When a customer picks your bid</p></div>
            <Switch data-testid="pref-bid-accepted" checked={(prefs.bid_accepted || "instant") !== "off"} onCheckedChange={(c) => setPrefs((p) => ({ ...p, bid_accepted: c ? "instant" : "off" }))} />
          </div>
        </Card>

        <Button onClick={save} disabled={saving} data-testid="save-profile-btn" className="bg-kinetic hover:bg-kinetic-hover text-white h-11 px-6">{saving ? "Saving…" : "Save profile"}</Button>
      </main>
    </div>
  );
}
